import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

import { IconButton } from '../IconButton'
import useCopyFeedback from './useCopyFeedback'
import './CopyIconButton.module.scss'

const COPY_ICON = 'far fa-copy'
const COPIED_ICON = 'fas fa-check'
const COPY_MESSAGE = { id: 'UIC.GENERAL.COPY', defaultMessage: 'Copy' }

const propTypes = {
  /** Text written to the clipboard when clicked. When empty/absent, no copy icon is rendered. */
  value: PropTypes.string,
  /** Id set on the rendered `<button>` element. */
  id: PropTypes.string,
  /** Optional wrapper class — e.g. spacing utilities. */
  className: PropTypes.string,
  /** Called with `value` after a copy is attempted. */
  onCopy: PropTypes.func,
}

/**
 * Copy-to-clipboard icon button with built-in feedback: the ghost copy glyph flips to a green check,
 * and a "Copied" toast appears at the bottom-center of the screen for a couple of seconds. The copy +
 * toast behaviour lives in the shared `useCopyFeedback` hook, so this and the labelled `CopyButton`
 * stay consistent. Drop it anywhere a value should be copyable; in a table wrap it in `HoverReveal` to
 * reveal it on row hover.
 */
function CopyIconButton({ value, id = undefined, className = '', onCopy = null }) {
  const intl = useIntl()
  const { copied, copy, toast } = useCopyFeedback()

  const handleClick = useCallback(
    (event) => {
      copy(value, event)
      onCopy?.(value)
    },
    [copy, value, onCopy],
  )

  // Nothing to copy — render no affordance at all, so an absent field shows no copy icon (AP-14188).
  if (value == null || value === '') {
    return null
  }

  return (
    <span className={clsx('Uic-CopyIconButton', copied && 'Uic-CopyIconButton-copied', className)}>
      <IconButton
        id={id}
        icon={copied ? COPIED_ICON : COPY_ICON}
        variant="ghost"
        label={intl.formatMessage(COPY_MESSAGE)}
        onClick={handleClick}
      />
      {toast}
    </span>
  )
}

CopyIconButton.propTypes = propTypes

export default React.memo(CopyIconButton)
